import {
  Flow,
  type Maybe,
  type MonetaryValue,
  type MoneyFlow,
} from "@digits-graphql/frontend/graphql-bearer"
import envHelper from "@digits-shared/helpers/envHelper"
import monetaryValueHelper from "@digits-shared/helpers/monetaryValueHelper"
import numberHelper, {
  type CurrencyFormatOptions,
  type CurrencyReturnType,
  CurrencyStyle,
} from "@digits-shared/helpers/numberHelper"

export default {
  calculateOpeningMoneyFlow(
    priorFlow: MoneyFlow | undefined,
    current: MoneyFlow,
    deltaPrevious?: Maybe<number>,
    isBalanceSheetCategory?: boolean
  ): MoneyFlow {
    const {
      value: { amount: currentAmount },
    } = current

    if (!isBalanceSheetCategory) {
      return buildZeroMoneyFlow()
    }

    if (priorFlow) {
      // build zero money flow if prior amount is zero, this sets isNormal to true
      return priorFlow.value.amount == 0 ? buildZeroMoneyFlow() : priorFlow
    }

    if (deltaPrevious === undefined || deltaPrevious === null) {
      return current
    }

    // Is previous amount zero calculation to avoid floating point errors
    // 1. difference between current and previous
    // 2. converted to currency units
    // 3. multiplied by 100 to calculate to the penny -- Unclear if the penny calculation will be correct for all currencies
    // 4. truncated to remove any fractional pennies
    const isPreviousAmountZero =
      Math.trunc(((currentAmount - deltaPrevious) / current.value.currencyMultiplier) * 100) === 0

    if (isPreviousAmountZero) {
      return buildZeroMoneyFlow()
    }

    const previousAmount = currentAmount - deltaPrevious
    const previousValue = {
      ...current.value,
      amount: previousAmount,
    }

    return {
      value: previousValue,
      businessFlow:
        Math.sign(currentAmount === 0 ? 1 : currentAmount) === Math.sign(previousAmount)
          ? current.businessFlow // same amount signs, same business flow
          : invertBusinessFlow(current.businessFlow), // different amount signs, invert business flow
      isNormal:
        Math.sign(currentAmount === 0 ? 1 : currentAmount) === Math.sign(previousAmount)
          ? current.isNormal // same amount sign, same isNormal
          : !current.isNormal, // different amount signs, invert isNormal
    }
  },

  amountBusinessFlow(amount: number): Flow {
    if (amount >= 0) return Flow.Inbound
    return Flow.Outbound
  },

  toMonetaryValue(moneyFlow?: MoneyFlow | null): MonetaryValue {
    const flow = moneyFlow ?? buildZeroMoneyFlow()
    return {
      amount: flow.isNormal ? Math.abs(flow.value.amount) : Math.abs(flow.value.amount) * -1,
      currencyMultiplier: flow.value.currencyMultiplier,
      iso4217CurrencyCode: flow.value.iso4217CurrencyCode,
    }
  },

  toMonetaryUnmultipliedValue(moneyFlow?: MoneyFlow | null): number | undefined {
    if (!moneyFlow) return undefined
    const monetaryValue = this.toMonetaryValue(moneyFlow)
    return monetaryValueHelper.unmultipliedAmount(monetaryValue)
  },

  /**
   * Converts large int to USD currency format
   *
   * We store amounts as int64 multiplied by 1,000,000 to give us 6 decimal point precision
   * in our db to avoid inaccuracy of floats - See https://goo.gl/my1oVc
   *
   * @param {MoneyFlow} moneyFlow is comprised of a amount, a
   * multiplier (adjustment of the amount to convert back to float), and ISO 4217 currency
   * code for the amount
   * @param {CurrencyFormatOptions} options optional flags for formatting return
   * Decimal precision is ignored for all integers, n, such that abs(n) < 1000.
   * @return {string|array} if CurrencyFormatOptions.returnInParts is passed along with
   * CurrencyStyle.Summary, the return will be an array of the abbreviation and
   * abbreviation symbol i.e. k, m, b, etc.
   */
  currency<T extends CurrencyFormatOptions>(
    moneyFlow?: MoneyFlow | null,
    options: T = { style: CurrencyStyle.Detail } as T
  ): CurrencyReturnType<T> {
    return numberHelper.currency(this.toMonetaryValue(moneyFlow), options)
  },

  addValues(...values: MoneyFlow[]) {
    return values.reduce((agg, val) => this.add(agg, val), this.buildZeroMoneyFlow())
  },

  add(a: MoneyFlow | undefined | null, b: MoneyFlow | undefined | null): MoneyFlow {
    const aValue = this.toMonetaryValue(a)
    const bValue = this.toMonetaryValue(b)
    const total = aValue.amount + bValue.amount

    if (total === 0) {
      return buildZeroMoneyFlow()
    }
    if (!a || aValue.amount === 0) {
      return b ?? buildZeroMoneyFlow()
    }
    if (!b || bValue.amount === 0) {
      return a ?? buildZeroMoneyFlow()
    }

    const flowMismatch =
      (a.businessFlow === b.businessFlow && a.isNormal !== b.isNormal) ||
      (a.businessFlow !== b.businessFlow && a.isNormal === b.isNormal)

    if (flowMismatch && envHelper.isDevelopment()) {
      console.error(
        "Flow mismatch while summing money flows. This means the sum is non-deterministic.",
        a,
        b
      )
    }

    const invert = (aValue.amount < 0 && total > 0) || (aValue.amount > 0 && total < 0)
    return {
      value: {
        amount: total,
        currencyMultiplier: aValue.currencyMultiplier,
        iso4217CurrencyCode: aValue.iso4217CurrencyCode,
      },
      businessFlow: invert ? invertBusinessFlow(a.businessFlow) : a.businessFlow,
      isNormal: invert ? !a.isNormal : a.isNormal,
    }
  },

  divide(a: MoneyFlow | undefined | null, b: number): MoneyFlow {
    const flow = a ?? buildZeroMoneyFlow()
    if (b === 0) return flow
    return {
      ...flow,
      value: {
        ...flow.value,
        amount: flow.value.amount / b,
      },
    }
  },

  invert(flow: MoneyFlow): MoneyFlow {
    if (flow.value.amount === 0) return buildZeroMoneyFlow()

    return {
      value: flow.value,
      businessFlow: invertBusinessFlow(flow.businessFlow),
      isNormal: !flow.isNormal,
    }
  },

  subtract(a: MoneyFlow | undefined, b: MoneyFlow | undefined): MoneyFlow {
    return this.add(a, this.invert(b ?? buildZeroMoneyFlow()))
  },

  percentage(current: MoneyFlow, next: MoneyFlow) {
    return monetaryValueHelper.percentage(this.toMonetaryValue(current), this.toMonetaryValue(next))
  },

  /**
   * Calculates the delta percentage between two MoneyFlow objects
   * @param current The current MoneyFlow
   * @param prior The prior MoneyFlow to compare against
   * @returns The percentage difference between the two flows
   */
  deltaPercentage(
    current: MoneyFlow | undefined | null,
    prior: MoneyFlow | undefined | null
  ): number {
    if (!current || !prior) return 0

    const currentValue = this.toMonetaryValue(current)
    const priorValue = this.toMonetaryValue(prior)

    if (priorValue.amount === 0) return 0

    const delta = ((currentValue.amount - priorValue.amount) / Math.abs(priorValue.amount)) * 100
    return Math.abs(delta) <= 10 ? parseFloat(delta.toFixed(2)) : Math.trunc(delta)
  },

  equals(a: MoneyFlow, b: MoneyFlow): boolean {
    const aValue = this.toMonetaryValue(a)
    const bValue = this.toMonetaryValue(b)

    return (
      aValue.amount === bValue.amount &&
      a.isNormal === b.isNormal &&
      a.businessFlow === b.businessFlow
    )
  },

  /**
   * Compares two MoneyFlow objects
   * @returns -1 if a > b, 0 if a === b, 1 if b > a
   */
  compare(a: MoneyFlow | undefined, b: MoneyFlow | undefined): number {
    if (!a && !b) return 0
    if (!a) return 1
    if (!b) return -1

    const aValue = this.toMonetaryValue(a)
    const bValue = this.toMonetaryValue(b)

    if (aValue.amount === bValue.amount) {
      if (a.isNormal === b.isNormal && a.businessFlow === b.businessFlow) {
        return 0
      }
      // If amounts are equal but other properties differ, consider them equal
      return 0
    }

    return aValue.amount > bValue.amount ? -1 : 1
  },

  calculateMean(...values: MoneyFlow[]) {
    const valuesTotal = this.addValues(...values)
    return this.divide(valuesTotal, values.length)
  },

  buildZeroMoneyFlow,
}

function invertBusinessFlow(flow: Flow): Flow {
  switch (flow) {
    case Flow.Inbound:
      return Flow.Outbound
    case Flow.Outbound:
      return Flow.Inbound
    default:
      return Flow.UnknownFlow
  }
}

export function buildZeroMoneyFlow(businessFlow = Flow.Inbound): MoneyFlow {
  return {
    value: {
      amount: 0,
      currencyMultiplier: 1e6,
      iso4217CurrencyCode: "USD",
    },
    businessFlow,
    isNormal: true,
  }
}
